import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import { SelectionButton } from '~/bootcamp/Activities/Fractions/components/SelectionButton'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generateDenominatorNumerator } from './helpers'

const MIN_NUMERATOR = 1
const MAX_NUMERATOR = 4
const MAX_DENOMINATOR = 5
const MAX_HISTORY_LENGTH = 3

function FractionName({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC: SubmitButton,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('bootcamp')
  const [isAnswerDenominator, setIsAnswerDenominator] = useState(true)
  const [hasSelectedDenominator, setHasSelectedDenominator] = useState<boolean | null>(null)
  const [numeratorHistory, setNumeratorHistory] = useState<number[]>([])
  const [{ denominator, numerator }, setNumbers] = useState({
    denominator: 1,
    numerator: 1,
  })

  // The asked number is on screen; isAnswerDenominator (the verdict) never rides the emit.
  // numerator === denominator only on the placeholder first render.
  useEmitInstance(
    onInstanceChange,
    numerator !== denominator
      ? `\\frac{${numerator}}{${denominator}}; where is the number ` +
          `${isAnswerDenominator ? denominator : numerator} placed? options: numerator, denominator`
      : null,
  )

  useEffect(() => {
    setHasSelectedDenominator(null)
    setNumbers(generateDenominatorNumerator(MIN_NUMERATOR, MAX_NUMERATOR, MAX_DENOMINATOR, numeratorHistory))
    setIsAnswerDenominator(Math.random() < 0.5)
  }, [numeratorHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [hasSelectedDenominator, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = hasSelectedDenominator === isAnswerDenominator
    onAnswerStateChange(isCorrect)
  }

  const handleOnOptionClick = (isDenominator: boolean): void => {
    if (isDenominator) {
      setHasSelectedDenominator(hasSelectedDenominator === true ? null : true)
    } else {
      setHasSelectedDenominator(hasSelectedDenominator === false ? null : false)
    }
  }

  const handleOnNextQuestionClick = (): void => {
    setNumeratorHistory((prevState) => {
      return [numerator, ...prevState].slice(0, MAX_HISTORY_LENGTH)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper className="flex-col">
        <p className="text-black font-normal text-md mb-6">
          {t('fractions.number_placed_in', { value: isAnswerDenominator ? denominator : numerator })}
        </p>
        <div className="w-6 flex flex-col items-center">
          {numerator}
          <Divider />
          {denominator}
        </div>
        <div className="w-full flex justify-center mt-5 md:mt-12">
          <div className="px-3 w-43">
            <SelectionButton
              isSelected={hasSelectedDenominator === false}
              isWrong={hasSelectedDenominator === false && answerState === false}
              onClick={() => {
                handleOnOptionClick(false)
              }}
              size="medium"
            >
              {t('fractions.numerator')}
            </SelectionButton>
          </div>
          <div className="px-3 w-43">
            <SelectionButton
              isSelected={hasSelectedDenominator === true}
              isWrong={hasSelectedDenominator === true && answerState === false}
              onClick={() => {
                handleOnOptionClick(true)
              }}
              size="medium"
            >
              {t('fractions.denominator')}
            </SelectionButton>
          </div>
        </div>
      </FractionsActivityWrapper>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={() => setHasSelectedDenominator(null)}
          correctAnswer={answerState ?? false}
          formValid={hasSelectedDenominator !== null}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default FractionName
